import {
  ArrowUpRight,
  BookOpenText,
  Check,
  Download,
  ExternalLink,
  FileText,
  Info,
  LoaderCircle,
  Maximize2,
  MessageSquarePlus,
  MoveDiagonal2,
  Sparkles,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type {
  HubCatalog,
  HubModule,
  HubSearchResult,
  HubSourceBrief,
} from "../lib/types";
import { SafeHtml } from "./SafeHtml";

type McsSourcePreviewModalProps = {
  result: HubSearchResult;
  catalog: HubCatalog;
  module: HubModule | null;
  loading: boolean;
  error: string;
  brief: HubSourceBrief | null;
  briefLoading: boolean;
  briefError: string;
  selectedForChat: boolean;
  selectionFull: boolean;
  onClose: () => void;
  onChatToggle: (result: HubSearchResult) => void;
  onOpenResult: (result: HubSearchResult) => void;
  onPreviewResult: (result: HubSearchResult) => void;
};

export function McsSourcePreviewModal({
  result,
  catalog,
  module,
  loading,
  error,
  brief,
  briefLoading,
  briefError,
  selectedForChat,
  selectionFull,
  onClose,
  onChatToggle,
  onOpenResult,
  onPreviewResult,
}: McsSourcePreviewModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const section = catalog.sections.find((candidate) => candidate.id === result.sectionId);
  const chapter = module?.chapters.find((candidate) => candidate.id === result.chapterId);
  const previewMarkdown = useMemo(() => {
    if (!brief) return "";
    if (brief.coverage === "ai-summary" || brief.markdown.length <= 24_000) return brief.markdown;
    return `${brief.markdown.slice(0, 24_000).replace(/\s+\S*$/, "").trim()}\n\n---\n\n_Preview shortened for readability. Selected-source chat receives the complete source brief._`;
  }, [brief]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    document.body.classList.add("source-preview-open");
    return () => {
      document.body.classList.remove("source-preview-open");
      if (dialog.open) dialog.close();
    };
  }, []);

  const moduleResults = useMemo(
    () => catalog.modules
      .filter((candidate) => candidate.sectionId === result.sectionId)
      .map<HubSearchResult>((candidate) => ({
        id: candidate.id,
        type: "module",
        title: candidate.title,
        sectionId: candidate.sectionId,
        sectionTitle: section?.title || "GRAP guidance",
        moduleId: candidate.id,
        moduleTitle: candidate.title,
        excerpt: candidate.descriptionText,
        score: 0,
      })),
    [catalog.modules, result.sectionId, section?.title],
  );

  function closeDialog() {
    dialogRef.current?.close();
  }

  function resetSize() {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.style.width = "min(860px, 80vw)";
    dialog.style.height = "min(82vh, 900px)";
  }

  return (
    <dialog
      ref={dialogRef}
      className="mcs-preview-modal"
      aria-label={`GRAP source preview: ${result.title}`}
      onClose={onClose}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        const outside = event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
        if (outside) closeDialog();
      }}
    >
      <header className="mcs-preview-modal__toolbar">
        <div><MoveDiagonal2 size={17} /><span>Source preview</span><small>Resize from the bottom-right corner</small></div>
        <div>
          <button type="button" onClick={resetSize} aria-label="Reset preview size"><Maximize2 size={17} /></button>
          <button type="button" autoFocus onClick={closeDialog} aria-label="Close source preview"><X size={18} /></button>
        </div>
      </header>
      <div className="mcs-preview-modal__body">
        <div className="preview-source-heading">
          <span className={`source-type source-type--${result.type}`}>
            {result.type === "module" ? <BookOpenText size={14} /> : <FileText size={14} />}
            {result.type === "chapter" ? "Chapter" : result.type === "module" ? "Topic" : result.type === "resource" ? result.format || "Resource" : "Knowledge area"}
          </span>
          <p>{result.sectionTitle}{result.moduleTitle && result.moduleTitle !== result.title ? ` · ${result.moduleTitle}` : ""}</p>
          <h2>{result.title}</h2>
          {result.excerpt && !brief ? <p className="preview-source-heading__summary">{result.excerpt}</p> : null}
          {result.type === "resource" && result.url && !brief ? (
            <button className="primary-action" type="button" onClick={() => onOpenResult(result)}>
              <Download size={17} /> Open original resource
            </button>
          ) : null}
        </div>

        {loading ? <div className="preview-loading"><LoaderCircle className="spin" size={25} /> Loading source content…</div> : null}
        {error ? <p className="preview-error">{error}</p> : null}

        {briefLoading ? (
          <div className="source-brief-loading"><LoaderCircle className="spin" size={20} /> Loading the source summary used by chat…</div>
        ) : null}
        {!briefLoading && brief ? (
          <section className="source-brief" aria-label="Source summary">
            <aside className="source-brief__rail">
              <span className={`source-brief__status source-brief__status--${brief.coverage}`}>
                {brief.coverage === "ai-summary" ? <Sparkles size={14} /> : <FileText size={14} />}
                {brief.coverage === "ai-summary" ? "AI summary" : "Source-derived"}
              </span>
              <dl>
                <div><dt>Knowledge area</dt><dd>{brief.sectionTitle}</dd></div>
                <div><dt>Source type</dt><dd>{sourceTypeLabel(brief.kind)}</dd></div>
                <div><dt>Brief size</dt><dd>{formatTokens(brief.summaryEstimatedTokens)}</dd></div>
                <div><dt>Brief coverage</dt><dd>{brief.isLarge ? "Representative" : "Complete"}</dd></div>
              </dl>
              <button
                className="primary-action source-brief__chat"
                type="button"
                aria-pressed={selectedForChat}
                disabled={!selectedForChat && selectionFull}
                onClick={() => onChatToggle(result)}
              >
                {selectedForChat ? <Check size={15} /> : <MessageSquarePlus size={15} />}
                {selectedForChat ? "Included in chat" : "Add to chat"}
              </button>
              {isSafePublicUrl(brief.sourceUrl) ? (
                <a className="secondary-action source-brief__open" href={brief.sourceUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={15} /> Open source
                </a>
              ) : null}
            </aside>
            <div className="source-brief__main">
              <header><span><Sparkles size={18} /></span><div><p>Grounded source context</p><h3>Source summary</h3></div></header>
              <div className="source-brief__markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>{previewMarkdown}</ReactMarkdown>
              </div>
            </div>
          </section>
        ) : null}
        {!briefLoading && briefError ? (
          <p className="source-brief-error"><Info size={16} /> No generated summary is available for this source. The original published content remains accessible below.</p>
        ) : null}

        {!loading && !error && result.type === "section" && section ? (
          <section className="preview-content">
            {!brief ? (section.summaryHtml ? <SafeHtml html={section.summaryHtml} className="rich-content" /> : <p>{section.summaryText || "Browse the topics in this knowledge area."}</p>) : null}
            <h3>Topics in this area</h3>
            <div className="preview-topic-list">
              {moduleResults.map((candidate) => (
                <button type="button" key={candidate.id} onClick={() => onPreviewResult(candidate)}>
                  <BookOpenText size={17} /><span>{candidate.title}</span><ArrowUpRight size={15} />
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && !error && result.type === "module" && module ? (
          <section className="preview-content">
            {!brief && module.descriptionHtml ? <SafeHtml html={module.descriptionHtml} className="rich-content" /> : null}
            {!brief && !module.descriptionHtml ? <p>{module.descriptionText || "No standalone description was supplied for this topic."}</p> : null}
            {module.chapters.length > 0 ? <h3>Chapters</h3> : null}
            <div className="preview-topic-list">
              {module.chapters.map((candidate) => (
                <button
                  type="button"
                  key={candidate.id}
                  onClick={() => onPreviewResult({
                    id: candidate.id,
                    type: "chapter",
                    title: candidate.title,
                    sectionId: result.sectionId,
                    sectionTitle: result.sectionTitle,
                    moduleId: module.id,
                    moduleTitle: module.title,
                    chapterId: candidate.id,
                    excerpt: candidate.text.slice(0, 320),
                    score: 0,
                  })}
                >
                  <FileText size={17} /><span>{candidate.title}</span><ArrowUpRight size={15} />
                </button>
              ))}
            </div>
            {module.resources.length > 0 ? <h3>Resources</h3> : null}
            <div className="preview-topic-list">
              {module.resources.map((resource) => (
                <a href={resource.originalUrl || resource.url} target="_blank" rel="noopener noreferrer" key={resource.id}>
                  <Download size={17} /><span>{resource.title}</span><small>{resource.format}</small>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && !error && result.type === "chapter" && chapter ? (
          <section className="preview-content">
            {!brief ? <SafeHtml html={chapter.html} className="rich-content" /> : null}
            {chapter.resources.length > 0 ? (
              <>
                <h3>Related resources</h3>
                <div className="preview-topic-list">
                  {chapter.resources.map((resource) => (
                    <a href={resource.originalUrl || resource.url} target="_blank" rel="noopener noreferrer" key={resource.id}>
                      <Download size={17} /><span>{resource.title}</span><small>{resource.format}</small>
                    </a>
                  ))}
                </div>
              </>
            ) : null}
          </section>
        ) : null}

        {!loading && !error && result.type === "resource" && !brief ? (
          <section className="preview-content preview-content--resource">
            <FileText size={40} aria-hidden="true" />
            <h3>Original public GRAP resource</h3>
            <p>This file is part of {result.moduleTitle || result.sectionTitle}. Open it in a new tab to review the complete source.</p>
            <button className="primary-action" type="button" onClick={() => onOpenResult(result)}>
              <ArrowUpRight size={17} /> Open source
            </button>
          </section>
        ) : null}
      </div>
      <span className="mcs-preview-modal__resize-cue" aria-hidden="true" />
    </dialog>
  );
}

function sourceTypeLabel(value: HubSourceBrief["kind"]) {
  if (value === "section") return "Knowledge area";
  if (value === "module") return "Topic";
  if (value === "chapter") return "Chapter";
  return "Document";
}

function formatTokens(value: number) {
  return value >= 1_000 ? `${(value / 1_000).toFixed(1)}k tokens` : `${value} tokens`;
}

function isSafePublicUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}
